import { User } from '../contexts/AuthContext';

export type UserType = 'customer' | 'provider' | 'admin' | 'unknown';

/**
 * Determines the user type based on their roles
 * 
 * @param user - The authenticated user object
 * @returns UserType - The type of user (customer, provider, admin, unknown)
 */
export const getUserType = (user: User | null): UserType => {
  if (!user) {
    return 'unknown';
  }

  // Handle cases where roles might be undefined, null, or not an array
  const userRoles = user.roles;
  if (!userRoles || !Array.isArray(userRoles) || userRoles.length === 0) {
    console.log('User has no roles or roles is not an array:', userRoles);
    return 'customer'; // Default to customer for users without roles
  }

  const roles = userRoles.map(role => role.toLowerCase());

  // Check for admin roles (highest priority)
  if (roles.some(role => 
    role.includes('admin') || 
    role.includes('superuser') || 
    role.includes('manager')
  )) {
    return 'admin';
  }

  // Check for provider roles
  if (roles.some(role => 
    role.includes('provider') || 
    role.includes('pandit') || 
    role.includes('service_provider') ||
    role.includes('vendor')
  )) {
    return 'provider';
  }

  // Check for customer roles
  if (roles.some(role => 
    role.includes('customer') || 
    role.includes('user') || 
    role.includes('client')
  )) {
    return 'customer';
  }

  // Default to customer for authenticated users without specific roles
  return 'customer';
};

/**
 * Checks if the user has a specific role
 * 
 * @param user - The authenticated user object
 * @param role - The role to check for
 * @returns boolean - Whether the user has the specified role
 */
export const hasRole = (user: User | null, role: string): boolean => {
  if (!user) {
    return false;
  }

  const userRoles = user.roles;
  if (!userRoles || !Array.isArray(userRoles)) {
    return false;
  }

  return userRoles.some(userRole => 
    userRole.toLowerCase().includes(role.toLowerCase())
  );
};

/**
 * Gets the appropriate dashboard path for a user
 * 
 * @param user - The authenticated user object
 * @returns string - The dashboard path for the user
 */
export const getDashboardPath = (user: User | null): string => {
  const userType = getUserType(user);

  switch (userType) {
    case 'admin':
      return '/dashboard/provider';
    case 'provider':
      // Check provider status to route appropriately
      if (user?.status === 'UNDER_REVIEW') {
        console.log('🚦 Routing under-review provider to under-review page');
        return '/provider/under-review';
      } else if (user?.status === 'ACTIVE') {
        return '/dashboard/provider';
      } else {
        // For new providers without status, route to under-review
        console.log('🚦 Routing new provider without status to under-review page');
        return '/provider/under-review';
      }
    case 'customer':
    default:
      return '/'; // Homepage for customers and unknown users
  }
};

/**
 * Checks if user can access provider features
 * 
 * @param user - The authenticated user object
 * @returns boolean - Whether user has provider access
 */
export const hasProviderAccess = (user: User | null): boolean => {
  const userType = getUserType(user);
  return userType === 'provider' || userType === 'admin';
};

/**
 * Checks if user can access customer features
 * 
 * @param user - The authenticated user object
 * @returns boolean - Whether user has customer access
 */
export const hasCustomerAccess = (user: User | null): boolean => {
  const userType = getUserType(user);
  return userType === 'customer' || userType === 'admin';
};